// =============================================================================
// TrackerTable.jsx — versão original restaurada
// =============================================================================

import React, { useState, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, Paper, Chip, IconButton, Typography,
  Box, Tooltip, LinearProgress,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import celfinetLogo      from '../../../assets/Cyient_Slate_Std_RGB.png';

const fmtDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, yyyy, a, b] = dash;
    return Number(a) > 12 ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

// Timestamp para ordenação — reutiliza a normalização do fmtDate (que já
// lida com "DD/MM/YYYY" e datas trocadas "YYYY-DD-MM") em vez de comparar a
// string em bruto. Sem isto, o Start/End ordenava mal porque comparava
// strings em formatos diferentes carácter a carácter, ignorando na prática
// o ano. Datas em falta/invalidas vão sempre para o fim.
const toTimestamp = (raw) => {
  if (!raw) return Infinity;
  const t = Date.parse(fmtDate(raw));
  return Number.isNaN(t) ? Infinity : t;
};

// Status mostrado ao utilizador — "Expired" é calculado, nunca gravado.
// Closed mantém-se Closed independentemente da data (já foi encerrada).
// Sem data de fim (atividade em aberto) nunca expira.
const getDisplayStatus = (entry) => {
  if ((entry.status ?? 'Open') === 'Closed') return 'Closed';
  return toTimestamp(entry.endDate) < Date.now() ? 'Expired' : 'Open';
};

const COLUMNS = [
  { id: 'title',       label: 'Activity', sortKey: (e) => e.title?.toLowerCase() ?? '' },
  { id: 'teamName',    label: 'Team',     sortKey: (e) => e.teamName?.toLowerCase() ?? '' },
  { id: 'technology',  label: 'Tech',     sortKey: (e) => e.technology?.toLowerCase() ?? '' },
  { id: 'vendor',      label: 'Vendor',   sortKey: (e) => e.vendor?.toLowerCase() ?? '' },
  { id: 'feature',     label: 'Type',     sortKey: (e) => e.feature?.toLowerCase() ?? '' },
  { id: 'status',      label: 'Status',   sortKey: (e) => getDisplayStatus(e).toLowerCase() },
  { id: 'startDate',   label: 'Start',    sortKey: (e) => toTimestamp(e.startDate) },
  { id: 'endDate',     label: 'End',      sortKey: (e) => toTimestamp(e.endDate) },
  { id: 'ownerNames',  label: 'Owner',    sortKey: (e) => (e.ownerNames ?? []).join(', ').toLowerCase() },
  { id: 'phases',      label: 'Phases',   sortKey: null },
  { id: '_actions',    label: '',         sortKey: null },
];

const headerCellSx = {
  fontWeight: 700, fontSize: '0.68rem',
  textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
  borderBottom: 'none',
};

const TrackerTable = ({ entries, loading, onRowClick, onDelete, onCopy, showClosed = false, canEditEntry = () => true, canDeleteEntry = () => true, userRole = null, userId = null }) => {
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortCol,     setSortCol]     = useState('startDate');
  const [sortDir,     setSortDir]     = useState('asc');

  const handleSort = (colId) => {
    if (sortCol === colId) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(colId);
      setSortDir('asc');
    }
    setPage(0);
  };

  const displayed = useMemo(() => {
    const filtered = showClosed
      ? entries
      : entries.filter((e) => (e.status ?? 'Open') !== 'Closed');
    const col = COLUMNS.find((c) => c.id === sortCol);
    if (!col?.sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.sortKey(a);
      const bv = col.sortKey(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [entries, showClosed, sortCol, sortDir]);

  const start     = page * rowsPerPage;
  const paginated = rowsPerPage === -1 ? displayed : displayed.slice(start, start + rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {loading && <LinearProgress sx={{ height: 2 }} />}
      {!loading && entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities yet. Click <strong>+ New Activity</strong> to start.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Wrapper à parte da área com scroll — a marca d'água fica fora do
              conteúdo que faz scroll, senão com "Rows: All" (muitas linhas)
              ela só aparecia uma vez a meio de toda a lista em vez de ficar
              sempre centrada na zona visível enquanto se faz scroll. */}
          <Box sx={{ position: 'relative' }}>
            {/* Marca d'água — igual à do Gantt Chart */}
            <Box sx={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none', zIndex: 0,
              width: '30%', minWidth: '200px', opacity: 0.05,
              display: 'flex', justifyContent: 'center'
            }}>
              <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%' }} />
            </Box>
            <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small">
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id} sx={headerCellSx}>
                      {col.sortKey ? (
                        <TableSortLabel
                          active={sortCol === col.id}
                          direction={sortCol === col.id ? sortDir : 'asc'}
                          onClick={() => handleSort(col.id)}
                          sx={{ fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontWeight: 'inherit', color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.6) !important' }, '&.Mui-active': { color: 'white' } }}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((entry) => {
                  const isClosed = (entry.status ?? 'Open') === 'Closed';
                  const displayStatus = getDisplayStatus(entry);
                  return (
                    <TableRow
                      key={entry.id}
                      hover
                      onClick={() => onRowClick(entry)}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { border: 0 },
                        opacity: isClosed ? 0.55 : 1,
                      }}
                    >
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Tooltip title={entry.title} placement="top" enterDelay={400}>
                          <Typography variant="body2" fontWeight={600} noWrap
                            sx={{ color: isClosed ? 'text.disabled' : 'inherit' }}>
                            {entry.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.teamName || ''} placement="top" enterDelay={400} disableHoverListener={!entry.teamName}>
                          <span>{entry.teamName || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.technology || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.vendor || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.feature || ''} placement="top" enterDelay={400} disableHoverListener={!entry.feature}>
                          <span>{entry.feature || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          label={displayStatus}
                          size="small"
                          sx={{
                            fontSize: '0.68rem', height: 20, fontWeight: 600,
                            bgcolor: displayStatus === 'Closed'  ? 'grey.200'
                                   : displayStatus === 'Expired' ? 'warning.light'
                                   : 'success.light',
                            color:   displayStatus === 'Closed'  ? 'text.secondary'
                                   : displayStatus === 'Expired' ? 'warning.dark'
                                   : 'success.dark',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.startDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.endDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const owners = (entry.ownerNames?.length ? entry.ownerNames.join(', ') : entry.ownerName) || '—';
                          return (
                            <Tooltip title={owners} placement="top" enterDelay={400} disableHoverListener={owners === '—'}>
                              <span>{owners}</span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const phases = entry.phases || [];
                          const rollbackCount = phases.filter(ph => ph.label === 'Rollback').length;
                          const phaseCount = phases.length - rollbackCount;
                          return (
                            <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center' }}>
                              {phaseCount > 0 && (
                                <Chip label={`${phaseCount} Phase${phaseCount !== 1 ? 's' : ''}`}
                                  size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                              {rollbackCount > 0 && (
                                <Chip label={`${rollbackCount} Rollback${rollbackCount !== 1 ? 's' : ''}`}
                                  size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                              )}
                              {phases.length === 0 && (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {canEditEntry(entry) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onRowClick(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onCopy && (
                          <Tooltip title="Copy activity">
                            <IconButton size="small" onClick={() => onCopy(entry)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteEntry(entry) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(entry.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
          <TablePagination
            component="div"
            count={displayed.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: -1 }]}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        </>
      )}
    </Paper>
  );
};

export default TrackerTable;